import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, CreditCard, Download, FileText, Loader2, MessageCircle, Paperclip, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/common/SignaturePad";
import { NoShowBox, DiplomaBox } from "@/components/academy/PortalAfterCourse";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { money, fmtDate, fmtDateTime, ENROLLMENT_STATUS, waLink } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const brand = "bg-[#037782] hover:bg-[#025E67] text-white";
const INPUT_TYPES = { email: "email", phone: "tel", date: "date" };

function Section({ n, title, done, children, testId }) {
  return (
    <section className="bg-white border border-[#D5E5E5] rounded-2xl p-5 sm:p-6 space-y-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${done ? "bg-[#5B7A54] text-white" : "bg-[#F3EDE3] text-[#8A6A2F]"}`}>
          {done ? <Check className="w-4 h-4" /> : n}
        </span>
        <h2 className="font-serif text-xl text-[#1F2B2C]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function FieldInput({ field, value, onChange, token, onError }) {
  const [uploading, setUploading] = useState(false);
  const testId = `${ACADEMY.portalFieldPrefix}${field.key}`;
  if (field.type === "file") {
    const upload = async (ev) => {
      const file = ev.target.files?.[0];
      ev.target.value = "";
      if (!file) return;
      setUploading(true);
      onError("");
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/public/academy/enrollments/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange({ url: data.url, name: data.name });
      } catch (err) {
        onError(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        setUploading(false);
      }
    };
    return (
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#D5E5E5] bg-[#F8FCFC] text-sm cursor-pointer">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          {value?.url ? "Cambiar archivo" : "Subir foto o PDF"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} disabled={uploading} data-testid={testId} />
        </label>
        {value?.url && <span className="text-sm text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> {value.name || "Archivo cargado"}</span>}
      </div>
    );
  }
  if (field.type === "textarea") {
    return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  }
  if (field.type === "select") {
    return (
      <select className="w-full h-10 rounded-md border border-[#D5E5E5] bg-white px-3 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">Elegí una opción</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <Input type={INPUT_TYPES[field.type] || "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
}

// Fix W (2026-10-13): pagina personal del alumno (sin usuario ni contrasena,
// se entra con el link del email): paga la reserva o el saldo, completa sus
// datos, firma el contrato y baja el manual.
export default function EnrollmentPortalPage() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({});
  const [editingForm, setEditingForm] = useState(false);
  const [formError, setFormError] = useState("");
  const [savingForm, setSavingForm] = useState(false);
  const [signer, setSigner] = useState("");
  const [accept, setAccept] = useState(false);
  const [signError, setSignError] = useState("");
  const [signing, setSigning] = useState(false);
  const [payError, setPayError] = useState("");
  const [paying, setPaying] = useState("");
  const [polls, setPolls] = useState(0);
  const sigRef = useRef(null);

  const apply = useCallback((data) => {
    setP(data);
    setForm(data.form_data || {});
    setSigner((s) => s || data.student?.name || "");
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/academy/enrollments/${token}`);
      apply(data);
    } catch (err) {
      setLoadError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [token, apply]);

  useEffect(() => { load(); }, [load]);

  // Al volver de pagar con tarjeta el aviso de Square tarda unos segundos:
  // mientras haya un link pendiente se vuelve a consultar (hasta un minuto).
  useEffect(() => {
    if (!p?.pending_payment_url || polls >= 15) return undefined;
    const t = setTimeout(() => { setPolls((n) => n + 1); load(); }, 4000);
    return () => clearTimeout(t);
  }, [p, polls, load]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F4FAFA] flex items-center justify-center px-4" data-testid={ACADEMY.portalPage}>
        <p className="text-[#566768] text-center" data-testid={ACADEMY.portalError}>{loadError}</p>
      </div>
    );
  }
  if (!p) {
    return <div className="min-h-screen bg-[#F4FAFA] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#199095]" /></div>;
  }

  const acc = p.account || {};
  const st = ENROLLMENT_STATUS[p.status] || { label: p.status, cls: "" };
  const wa = waLink(p.contact_phone);
  const formOpen = !p.steps.form || editingForm;

  const pay = async (kind) => {
    setPayError("");
    setPaying(kind);
    try {
      const { data } = await api.post(`/public/academy/enrollments/${token}/pay`, { kind });
      window.location.assign(data.url);
    } catch (err) {
      setPayError(formatApiErrorDetail(err.response?.data?.detail));
      setPaying("");
    }
  };

  const sendForm = async () => {
    setFormError("");
    setSavingForm(true);
    try {
      const { data } = await api.post(`/public/academy/enrollments/${token}/form`, { data: form });
      apply(data);
      setEditingForm(false);
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingForm(false);
    }
  };

  const sign = async () => {
    setSignError("");
    if (signer.trim().length < 3) { setSignError("Escribí tu nombre completo"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { setSignError("Dibujá tu firma en el recuadro"); return; }
    if (!accept) { setSignError("Tenés que aceptar el contrato para firmarlo"); return; }
    setSigning(true);
    try {
      const { data } = await api.post(`/public/academy/enrollments/${token}/sign`, { signer_name: signer.trim(), signature: sigRef.current.getDataUrl(), accept: true });
      apply(data);
    } catch (err) {
      setSignError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4FAFA]" data-testid={ACADEMY.portalPage}>
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12 space-y-5">
        <header className="space-y-2">
          <p className="text-xs tracking-[0.2em] uppercase text-[#037782]">{p.academy_name}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1F2B2C]" data-testid={ACADEMY.portalCourseName}>{p.course?.name}</h1>
          <p className="text-[#566768]">{p.when}{p.edition?.location ? ` · ${p.edition.location}` : ""}</p>
          <p className="text-sm text-[#566768]">Hola {(p.student?.name || "").split(" ")[0]}. Esta es tu página personal de inscripción.</p>
          <span className={`inline-block text-xs px-2.5 py-1 rounded-full ${st.cls}`} data-testid={ACADEMY.portalStatus}>{st.label}</span>
        </header>

        {!p.active ? (
          <div className="bg-white border border-[#D5E5E5] rounded-2xl p-6 text-[#566768]">Esta inscripción ya no está activa. Si tenés dudas, escribinos.</div>
        ) : (
          <>
            {p.status === "confirmed" && (
              <div className="bg-[#E3EDE1] text-[#3F5A39] rounded-2xl p-5" data-testid={ACADEMY.portalConfirmed}>
                <p className="font-medium">¡Tu inscripción está confirmada!</p>
                <p className="text-sm mt-1">Te esperamos el {p.when}{p.edition?.location ? ` en ${p.edition.location}` : ""}.</p>
              </div>
            )}

            {/* Fix Y (2026-10-13): si no asistio, responder; despues del curso, practicas y diploma */}
            <NoShowBox token={token} data={p} onChanged={apply} />
            <DiplomaBox token={token} data={p} />

            <Section n={1} title="Reserva y pagos" done={p.steps.reservation} testId={ACADEMY.portalPaySection}>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-[#F4FAFA] p-3"><p className="text-xs text-[#566768]">Total</p><p className="font-medium">{money(acc.total)}</p></div>
                <div className="rounded-xl bg-[#F4FAFA] p-3"><p className="text-xs text-[#566768]">Pagado</p><p className="font-medium" data-testid={ACADEMY.portalPaid}>{money(acc.net_paid)}</p></div>
                <div className="rounded-xl bg-[#F4FAFA] p-3"><p className="text-xs text-[#566768]">Saldo</p><p className="font-medium" data-testid={ACADEMY.portalBalance}>{money(acc.balance)}</p></div>
              </div>
              {p.price_label && <p className="text-xs text-[#566768]">Tarifa: {p.price_label}</p>}
              {!p.steps.reservation ? (
                <div className="space-y-2">
                  <p className="text-sm text-[#1F2B2C]">Para guardar tu lugar pagá la reserva de <strong>{money(p.reservation_amount)}</strong>. Con la reserva paga se habilita el manual del curso.</p>
                  <Button className={brand} onClick={() => pay("reservation")} disabled={Boolean(paying)} data-testid={ACADEMY.portalPayReservationButton}>
                    {paying === "reservation" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pagar la reserva con tarjeta
                  </Button>
                </div>
              ) : acc.balance > 0.005 ? (
                <div className="space-y-2">
                  <p className="text-sm text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Reserva paga.</p>
                  <Button variant="outline" onClick={() => pay("balance")} disabled={Boolean(paying)} data-testid={ACADEMY.portalPayBalanceButton}>
                    {paying === "balance" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pagar el saldo ({money(acc.balance)}) con tarjeta
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Curso pagado al 100%.</p>
              )}
              {(acc.plan || []).length > 0 && (
                <div className="text-sm">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#566768] mb-1">Tu plan de pagos</p>
                  {acc.plan.map((x, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-[#E4F0F0] last:border-none">
                      <span>{fmtDate(x.due_date)}</span><span>{money(x.amount)}</span>
                      <span className={x.status === "paid" ? "text-[#5B7A54]" : x.status === "overdue" ? "text-[#A34B49]" : "text-[#566768]"}>{x.status === "paid" ? "Pagada" : x.status === "overdue" ? "Vencida" : "Pendiente"}</span>
                    </div>
                  ))}
                </div>
              )}
              {p.pending_payment_url && <p className="text-sm text-[#8A6A2F]" data-testid={ACADEMY.portalPendingNotice}>Si ya pagaste con tarjeta, en unos segundos se actualiza sola esta página.</p>}
              <p className="text-xs text-[#566768]">También podés pagar en la academia (efectivo, Zelle o tarjeta). Si ya pagaste con tarjeta y todavía no se ve, tocá actualizar.</p>
              <button type="button" onClick={load} className="text-xs text-[#8A6A2F] inline-flex items-center gap-1" data-testid={ACADEMY.portalRefreshButton}><RefreshCw className="w-3 h-3" /> Actualizar</button>
              {payError && <p className="text-sm text-destructive" data-testid={ACADEMY.portalPayError}>{payError}</p>}
            </Section>

            <Section n={2} title="Tus datos para la certificación" done={p.steps.form} testId={ACADEMY.portalFormSection}>
              {formOpen ? (
                <div className="space-y-4">
                  {p.form_fields.map((f) => (
                    <div key={f.key}>
                      <label className="text-sm text-[#566768] mb-1 block">{f.label}{f.required ? " *" : ""}</label>
                      <FieldInput field={f} value={form[f.key]} token={token} onError={setFormError} onChange={(v) => setForm((x) => ({ ...x, [f.key]: v }))} />
                    </div>
                  ))}
                  {formError && <p className="text-sm text-destructive" data-testid={ACADEMY.portalFormError}>{formError}</p>}
                  <Button className={brand} onClick={sendForm} disabled={savingForm} data-testid={ACADEMY.portalFormSubmit}>{savingForm ? "Enviando..." : "Enviar mis datos"}</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Recibimos tus datos.</p>
                  <Button variant="ghost" size="sm" onClick={() => setEditingForm(true)} data-testid={ACADEMY.portalFormEdit}>Corregir mis datos</Button>
                </div>
              )}
            </Section>

            <Section n={3} title="Contrato" done={p.steps.contract} testId={ACADEMY.portalContractSection}>
              {p.steps.contract ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Firmado por {p.contract_signer} el {fmtDateTime(p.contract_signed_at)}</p>
                  <a href={`${API}/public/academy/enrollments/${token}/contract`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#8A6A2F]" data-testid={ACADEMY.portalContractDownload}><FileText className="w-4 h-4" /> Ver mi contrato</a>
                </div>
              ) : !p.contract_text ? (
                <p className="text-sm text-[#566768]">El contrato todavía no está cargado. Te avisamos cuando puedas firmarlo.</p>
              ) : (
                <div className="space-y-4">
                  <div className="max-h-72 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed border border-[#D5E5E5] rounded-xl p-4 bg-[#F8FCFC]" data-testid={ACADEMY.portalContractText}>{p.contract_text}</div>
                  <div>
                    <label className="text-sm text-[#566768] mb-1 block">Tu nombre completo</label>
                    <Input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid={ACADEMY.portalSignerInput} />
                  </div>
                  <div>
                    <label className="text-sm text-[#566768] mb-1 block">Tu firma</label>
                    <SignaturePad ref={sigRef} />
                  </div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid={ACADEMY.portalAcceptCheckbox} />
                    Leí el contrato y lo acepto.
                  </label>
                  {signError && <p className="text-sm text-destructive" data-testid={ACADEMY.portalSignError}>{signError}</p>}
                  <Button className={brand} onClick={sign} disabled={signing} data-testid={ACADEMY.portalSignButton}>{signing ? "Firmando..." : "Firmar contrato"}</Button>
                </div>
              )}
            </Section>

            <section className="bg-white border border-[#D5E5E5] rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3" data-testid={ACADEMY.portalManualSection}>
              <div>
                <h2 className="font-serif text-xl text-[#1F2B2C]">Manual del curso</h2>
                <p className="text-sm text-[#566768]">{p.manual_available ? p.manual_name || "Listo para descargar." : p.steps.reservation ? "Todavía no está cargado." : "Se habilita cuando pagás la reserva."}</p>
              </div>
              {p.manual_available && (
                <a href={`${API}/public/academy/enrollments/${token}/manual`} className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm ${brand}`} data-testid={ACADEMY.portalManualDownload}><Download className="w-4 h-4" /> Descargar manual</a>
              )}
            </section>
          </>
        )}

        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-sm text-[#25A244] py-2"><MessageCircle className="w-4 h-4" /> ¿Dudas? Escribinos por WhatsApp</a>
        )}
      </div>
    </div>
  );
}
